import { Controller } from 'stimulus'

// Drives the "Put on every job" wizard on Settings -> Job Fields. Three
// steps, all data fetched as JSON: 1) analyze (spellings, detected type),
// 2) dropdown-option cleanup (keep / merge-into, dropdown clusters only),
// 3) impact preview. The final submit is a plain form POST so the redirect
// + flash come back like any settings action (same pattern as field-merge).
export default class extends Controller {
  static targets = ['modal', 'title', 'stepLabel', 'loading', 'error', 'stepPanel', 'spellings',
    'nameInput', 'typeChip', 'typeNote', 'conditionalNote', 'optionsBody', 'optionsFilter',
    'reviewList', 'backBtn', 'continueBtn', 'backgroundHint'];

  connect() {
    const auto = (this.data.get('autoKeys') || '').split(',').map(k => k.trim()).filter(Boolean)
    if (auto.length) this.start(auto)
  }

  open(ev) {
    this.start(JSON.parse(ev.currentTarget.dataset.keys))
  }

  start(keys) {
    this.keys = keys
    this.analysis = null
    this.optionRows = null
    this.modalTarget.classList.remove('hidden')
    this.showLoading('Looking at this field…')
    this.fetchJSON(this.data.get('analyzeUrl'), { 'keys[]': this.keys })
      .then((analysis) => {
        this.analysis = analysis
        if (analysis.blocked) {
          this.showError(analysis.blocked_reason || "This field can't go on every job.")
          return
        }
        this.renderStep1()
      })
      .catch(() => this.showError('Could not load this field. Refresh and try again.'))
  }

  close() {
    this.modalTarget.classList.add('hidden')
  }

  // ---- step navigation ----------------------------------------------------

  advance() {
    if (this.step === 1) {
      if (!this.nameInputTarget.value.trim()) {
        this.nameInputTarget.focus()
        return
      }
      if (this.analysis.resolved_type === 'dropdown') this.loadOptions()
      else this.loadPreview()
    } else if (this.step === 2) {
      this.loadPreview()
    } else {
      this.submit()
    }
  }

  back() {
    if (this.step === 3 && this.analysis.resolved_type === 'dropdown') this.renderStep2()
    else this.renderStep1()
  }

  // ---- step 1: name & type ------------------------------------------------

  renderStep1() {
    const a = this.analysis
    this.titleTarget.textContent = `Put “${a.display}” on every job`
    if (!this.nameInputTarget.value.trim()) this.nameInputTarget.value = a.display
    const rows = a.spellings.map(s => `
      <div class="flex items-center justify-between border border-gray-200 rounded px-3 py-2 mb-2 text-sm leading-5 text-gray-900">
        <span>${this.escape(s.spelling)}</span>
        <span class="text-xs text-gray-500">${s.jobs} jobs · ${s.templates} ${s.templates === 1 ? 'template' : 'templates'}</span>
      </div>`).join('')
    this.spellingsTarget.innerHTML = a.spellings.length > 1
      ? `<p class="text-sm leading-5 text-gray-500 mb-2">This field appears under ${a.spellings.length} spellings — they’ll be combined into one field on every job.</p>${rows}`
      : ''
    this.typeChipTarget.textContent = this.typeLabel(a.resolved_type)
    this.typeNoteTarget.textContent = a.type_note || ''
    const conditional = a.conditional_count > 0
    this.conditionalNoteTarget.classList.toggle('hidden', !conditional)
    if (conditional) {
      this.conditionalNoteTarget.textContent = `${a.conditional_count} of these fields use conditional show/hide rules, which every-job fields don’t have — the field will always be visible.`
    }
    this.showStep(1, `Step 1 of ${this.totalSteps()} · Name & type`, 'Continue')
  }

  // ---- step 2: dropdown options -------------------------------------------

  loadOptions() {
    if (this.optionRows) {
      this.renderStep2()
      return
    }
    this.showLoading('Collecting dropdown options…')
    this.fetchJSON(this.data.get('optionsUrl'), { 'keys[]': this.keys })
      .then(({ options }) => {
        this.optionRows = options
        this.renderStep2()
      })
      .catch(() => this.showError('Could not load the dropdown options. Refresh and try again.'))
  }

  renderStep2() {
    this.optionsBodyTarget.innerHTML = this.optionRows.map((row, i) => {
      const others = this.optionRows.filter(o => o.value !== row.value)
      const selects = others.map(o =>
        `<option value="${this.escape(o.value)}">Merge into “${this.escape(o.value)}”</option>`).join('')
      const orphan = row.in_list ? '' :
        ' <span class="inline-block text-xs text-yellow-800 bg-yellow-100 border border-yellow-200 rounded px-1">on jobs, not in any option list</span>'
      const similar = row.similar_to ? ` <button type="button"
          class="similar-chip inline-block text-xs text-yellow-800 bg-yellow-100 border border-yellow-200 rounded px-1 focus:outline-none"
          data-target-value="${this.escape(row.similar_to)}"
          data-action="click->job-field-promote#acceptSimilar"
          title="Click to merge into “${this.escape(row.similar_to)}”"></button>` : ''
      return `
        <tr data-value="${this.escape(row.value.toLowerCase())}">
          <td class="px-4 py-2 border-b border-gray-100 text-sm leading-5 text-gray-900 break-words">${this.escape(row.value)}${orphan}${similar}</td>
          <td class="px-4 py-2 border-b border-gray-100 text-sm leading-5 text-gray-500 text-right">${row.jobs}</td>
          <td class="px-4 py-2 border-b border-gray-100">
            <select class="form-select w-full text-sm py-1" data-source="${this.escape(row.value)}" data-index="${i}"
                    data-action="change->job-field-promote#syncRowChip">
              <option value="">Keep</option>
              ${selects}
            </select>
          </td>
        </tr>`
    }).join('')
    // Re-entering (Back from review) restores the collected selections.
    if (this.mapping) {
      this.optionsBodyTarget.querySelectorAll('select').forEach((sel) => {
        const to = this.mapping[sel.dataset.source]
        if (to) sel.value = to
      })
    }
    this.optionsBodyTarget.querySelectorAll('.similar-chip').forEach((chip) => {
      this.syncSimilarChip(chip, chip.closest('tr').querySelector('select'))
    })
    this.optionsFilterTarget.classList.toggle('hidden', this.optionRows.length <= 15)
    this.filterOptions()
    this.showStep(2, `Step 2 of ${this.totalSteps()} · Dropdown options`, 'Continue')
  }

  // "similar to X" chip: one click sets the row's merge select to the
  // suggested target; clicking again resets it to Keep.
  acceptSimilar(ev) {
    const chip = ev.currentTarget
    const select = chip.closest('tr').querySelector('select')
    const target = chip.dataset.targetValue
    select.value = select.value === target ? '' : target
    this.syncSimilarChip(chip, select)
  }

  syncRowChip(ev) {
    const chip = ev.currentTarget.closest('tr').querySelector('.similar-chip')
    if (chip) this.syncSimilarChip(chip, ev.currentTarget)
  }

  syncSimilarChip(chip, select) {
    const target = chip.dataset.targetValue
    const merged = select && select.value === target
    chip.textContent = merged ? `merging into “${target}”` : `similar to “${target}”`
    chip.classList.toggle('text-yellow-800', !merged)
    chip.classList.toggle('bg-yellow-100', !merged)
    chip.classList.toggle('border-yellow-200', !merged)
    chip.classList.toggle('text-green-800', merged)
    chip.classList.toggle('bg-green-100', merged)
    chip.classList.toggle('border-green-200', merged)
  }

  filterOptions() {
    const query = this.optionsFilterTarget.value.trim().toLowerCase()
    this.optionsBodyTarget.querySelectorAll('tr').forEach((tr) => {
      const el = tr
      el.style.display = !query || el.dataset.value.includes(query) ? '' : 'none'
    })
  }

  collectMapping() {
    const mapping = {}
    this.optionsBodyTarget.querySelectorAll('select').forEach((sel) => {
      if (sel.value) mapping[sel.dataset.source] = sel.value
    })
    return mapping
  }

  // ---- step 3: review -----------------------------------------------------

  loadPreview() {
    this.mapping = this.analysis.resolved_type === 'dropdown' ? this.collectMapping() : {}
    this.showLoading('Calculating impact…')
    const params = { 'keys[]': this.keys, target: this.nameInputTarget.value.trim() }
    this.fetchJSON(this.data.get('previewUrl'), params, this.mapping)
      .then((preview) => {
        if (preview.existing_column) {
          this.showError(`An every-job field named “${this.nameInputTarget.value.trim()}” already exists.`)
          return
        }
        this.renderStep3(preview)
      })
      .catch(() => this.showError('Could not calculate the impact. Refresh and try again.'))
  }

  renderStep3(preview) {
    const name = this.escape(this.nameInputTarget.value.trim())
    const blankJobs = Math.max(0, preview.total_jobs - preview.jobs_with_value - preview.conflicts)
    const items = []
    items.push(`<b>${name}</b> becomes a field on every job — a column on the jobs index and a filter in Metrics.`)
    let values = `<b>${preview.jobs_with_value.toLocaleString()} jobs</b> keep their current value`
    if (preview.rewrites > 0) values += ` (${preview.rewrites.toLocaleString()} values updated by the option cleanup)`
    values += `. ${blankJobs.toLocaleString()} other jobs start blank.`
    items.push(values)
    if (preview.conflicts > 0) {
      items.push(`<b>${preview.conflicts.toLocaleString()} jobs</b> hold two different values and will be left untouched for manual cleanup.`)
    }
    if (preview.templates > 0) {
      items.push(`Removed from <b>${preview.templates} ${preview.templates === 1 ? 'template' : 'templates'}</b> — new jobs get the every-job field instead.`)
    }
    if (preview.final_options && preview.final_options.length) {
      const shown = preview.final_options.slice(0, 12).map(o => this.escape(o))
      const extra = preview.final_options.length - shown.length
      items.push(`Dropdown options: ${shown.join(', ')}${extra > 0 ? `, and ${extra} more` : ''}.`)
    }
    items.push('The original template copies are archived, not deleted, and a record of the change is kept.')
    this.reviewListTarget.innerHTML = items.map(item => `
      <li class="flex items-start mb-2">
        <span class="flex-shrink-0 w-2 h-2 rounded-full bg-green-500 mt-1 mr-3"></span>
        <span>${item}</span>
      </li>`).join('')
    this.showStep(3, `Step 3 of ${this.totalSteps()} · Review`, 'Put on every job')
  }

  submit() {
    this.continueBtnTarget.disabled = true
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = this.data.get('promoteUrl')
    const add = (name, value) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    }
    add('authenticity_token', Rails.csrfToken())
    add('target', this.nameInputTarget.value.trim())
    this.keys.forEach(key => add('keys[]', key))
    Object.entries(this.mapping || {}).forEach(([from, to]) => add(`mapping[${from}]`, to))
    document.body.appendChild(form)
    form.submit()
  }

  // ---- plumbing -----------------------------------------------------------

  totalSteps() {
    return this.analysis && this.analysis.resolved_type === 'dropdown' ? 3 : 2
  }

  showStep(step, label, buttonText) {
    this.step = step
    this.loadingTarget.classList.add('hidden')
    this.errorTarget.classList.add('hidden')
    this.stepPanelTargets.forEach((panel) => {
      panel.classList.toggle('hidden', Number(panel.dataset.step) !== step)
    })
    // Two-step wizards (non-dropdown) show the review as "Step 2".
    this.stepLabelTarget.textContent = this.analysis.resolved_type === 'dropdown'
      ? label
      : label.replace('Step 3', 'Step 2')
    this.backBtnTarget.classList.toggle('hidden', step === 1)
    this.backgroundHintTarget.classList.toggle('hidden', step !== 3)
    this.continueBtnTarget.textContent = buttonText
    this.continueBtnTarget.classList.remove('hidden')
    this.continueBtnTarget.disabled = false
  }

  // Loading and error states leave Cancel as the only visible action — a
  // styled-but-dead Continue button reads as broken.
  showLoading(message) {
    this.loadingTarget.textContent = message
    this.loadingTarget.classList.remove('hidden')
    this.errorTarget.classList.add('hidden')
    this.stepPanelTargets.forEach(panel => panel.classList.add('hidden'))
    this.hideFooterActions()
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
    this.loadingTarget.classList.add('hidden')
    this.stepPanelTargets.forEach(panel => panel.classList.add('hidden'))
    this.hideFooterActions()
  }

  hideFooterActions() {
    this.continueBtnTarget.classList.add('hidden')
    this.continueBtnTarget.disabled = true
    this.backBtnTarget.classList.add('hidden')
    this.backgroundHintTarget.classList.add('hidden')
  }

  fetchJSON(url, params, mapping) {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([name, value]) => {
      if (Array.isArray(value)) value.forEach(v => query.append(name, v))
      else query.append(name, value)
    })
    Object.entries(mapping || {}).forEach(([from, to]) => query.append(`mapping[${from}]`, to))
    return fetch(`${url}?${query}`, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
  }

  typeLabel(type) {
    return { text: 'Text', date: 'Date', dropdown: 'Dropdown' }[type] || type
  }

  // Attribute-safe escaping (interpolated into both text and attributes).
  escape(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }
}
